import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import assetUserService from '../services/assetUserService';
import { FaExclamationTriangle, FaSpinner, FaCloudUploadAlt, FaTimes } from 'react-icons/fa';
import '../styles/BreakdownRequests.css';
import AssistantSidebar from './AssistantSidebar';
const BreakdownRequests = ({ user, onClose }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isCreatingIssue, setIsCreatingIssue] = useState(false);
  const [issueForm, setIssueForm] = useState({
    requiredSkill: '',
    severity: 'medium',
    checklist: []
  });

  useEffect(() => {
    fetchBreakdownRequests();
  }, []);

  const fetchBreakdownRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await assetUserService.getBreakdownRequests();
      if (response.success) {
        setRequests(response.requests || []);
      }
    } catch (err) {
      setError('Failed to load breakdown requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIssue = (request) => {
    setSelectedRequest(request);
    setIsCreatingIssue(true);
    setIssueForm({
      requiredSkill: '',
      severity: 'medium',
      checklist: []
    });
  };

  const handleSubmitIssue = async () => {
    if (!issueForm.requiredSkill) {
      alert('Please select a required skill');
      return;
    }

    try {
      setLoading(true);
      // Navigate to create issue page with pre-filled data
      navigate('/issues/create', {
        state: {
          assetId: selectedRequest.assetId._id,
          assetName: selectedRequest.assetId.assetName,
          description: selectedRequest.breakdownDescription,
          issueType: 'breakdown',
          requiredSkill: issueForm.requiredSkill,
          severity: issueForm.severity,
          requestId: selectedRequest._id
        }
      });
    } catch (err) {
      alert('Error creating issue: ' + err.message);
    } finally {
      setLoading(false);
      setSelectedRequest(null);
      setIsCreatingIssue(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isCreatingIssue && selectedRequest) {
    return (
      
      <div className="br-modal-overlay">
        <div className="br-modal">
          <div className="br-modal-header">
            <h2>Create Issue from Breakdown Request</h2>
            <button className="br-close-btn" onClick={() => setIsCreatingIssue(false)}>
              <FaTimes />
            </button>
          </div>

          <div className="br-modal-body">
            <div className="br-form-group">
              <label>Asset</label>
              <input 
                type="text" 
                value={selectedRequest.assetId.assetName} 
                disabled 
                className="br-input disabled"
              />
            </div>

            <div className="br-form-group">
              <label>Breakdown Description</label>
              <textarea 
                value={selectedRequest.breakdownDescription}
                disabled
                className="br-textarea disabled"
                rows={3}
              />
            </div>

            <div className="br-form-row">
              <div className="br-form-group">
                <label>Required Skill *</label>
                <select
                  value={issueForm.requiredSkill}
                  onChange={(e) => setIssueForm({ ...issueForm, requiredSkill: e.target.value })}
                  className="br-input"
                >
                  <option value="">Select a skill</option>
                  <option value="Electrical">Electrical</option>
                  <option value="Mechanical">Mechanical</option>
                  <option value="Software">Software</option>
                  <option value="Calibration">Calibration</option>
                  <option value="General Maintenance">General Maintenance</option>
                </select>
              </div>

              <div className="br-form-group">
                <label>Severity</label>
                <select
                  value={issueForm.severity}
                  onChange={(e) => setIssueForm({ ...issueForm, severity: e.target.value })}
                  className="br-input"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            <div className="br-form-group">
              <label>Reported By</label>
              <input 
                type="text" 
                value={selectedRequest.requestedBy.name}
                disabled 
                className="br-input disabled"
              />
            </div>

            <div className="br-form-group">
              <label>Date Reported</label>
              <input 
                type="text" 
                value={formatDate(selectedRequest.createdAt)}
                disabled 
                className="br-input disabled"
              />
            </div>
          </div>

          <div className="br-modal-footer">
            <button 
              className="br-btn br-btn-secondary" 
              onClick={() => setIsCreatingIssue(false)}
            >
              Cancel
            </button>
            <button 
              className="br-btn br-btn-primary"
              onClick={handleSubmitIssue}
              disabled={loading}
            >
              {loading ? <FaSpinner className="spin" /> : <FaCloudUploadAlt />}
              {loading ? 'Creating...' : 'Create Issue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="br-page">
      <AssistantSidebar user={user} />
      <div className="br-container">
        <div className="br-header">
          <div className="br-title">
            <FaExclamationTriangle className="br-icon" />
            <div>
              <h2>Breakdown Requests</h2>
              <p>Assets reported for breakdown - Create issues and assign technicians</p>
            </div>
          </div>
          {onClose && (
            <button className="br-close-btn" onClick={onClose}>
              <FaTimes />
            </button>
          )}
        </div>

      {loading ? (
        <div className="br-loading">
          <FaSpinner className="spin" />
          <p>Loading breakdown requests...</p>
        </div>
      ) : error ? (
        <div className="br-error">
          <p>{error}</p>
          <button className="br-btn br-btn-primary" onClick={fetchBreakdownRequests}>
            Retry
          </button>
        </div>
      ) : requests.length === 0 ? (
        <div className="br-empty">
          <FaExclamationTriangle />
          <p>No breakdown requests at the moment</p>
          <small>Assets will appear here when users report breakdowns</small>
        </div>
      ) : (
        <div className="br-requests-list">
          {requests.map((request) => (
            <div key={request._id} className="br-request-card">
              <div className="br-card-header">
                <div className="br-card-title">
                  <h3>{request.assetId?.assetName || 'Unknown Asset'}</h3>
                  <span className="br-badge">
                    {request.issueCreatedFromBreakdown ? '✓ Issue Created' : '⚠ Pending'}
                  </span>
                </div>
                <div className="br-card-meta">
                  <span className="br-date">{formatDate(request.createdAt)}</span>
                </div>
              </div>

              <div className="br-card-body">
                <div className="br-field">
                  <label>Reported By</label>
                  <p>{request.requestedBy?.name || 'Unknown'} ({request.requestedBy?.email || 'N/A'})</p>
                </div>

                <div className="br-field">
                  <label>Description</label>
                  <p className="br-description">{request.breakdownDescription}</p>
                </div>

                {request.assetId?.category && (
                  <div className="br-field">
                    <label>Category</label>
                    <p>{request.assetId.category}</p>
                  </div>
                )}
              </div>

              <div className="br-card-footer">
                {request.issueCreatedFromBreakdown ? (
                  <button 
                    className="br-btn br-btn-secondary"
                    onClick={() => navigate('/issues', { state: { issueId: request.issueCreatedFromBreakdown._id } })}
                  >
                    View Issue
                  </button>
                ) : (
                  <button 
                    className="br-btn br-btn-primary"
                    onClick={() => handleCreateIssue(request)}
                  >
                    Create Issue & Assign Technician
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default BreakdownRequests;
